
import React, { useEffect, useCallback } from 'react';
import { useCountry } from '@/contexts/country'; // Updated import path
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Globe } from 'lucide-react';
import CountryFlag from './CountryFlag';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCountryRealtime } from '@/hooks/useCountryRealtime';

const CountrySelector = () => {
  const { countryCode, availableCountries, setCountry, refreshCountries } = useCountry();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Memoize the refreshCountries callback to prevent unnecessary rerenders
  const memoizedRefreshCallback = useCallback(() => {
    console.log('Refreshing countries from real-time update');
    return refreshCountries();
  }, [refreshCountries]);
  
  // Set up realtime subscription to country changes
  useCountryRealtime(memoizedRefreshCallback);
  
  // Only refresh countries on initial mount
  useEffect(() => {
    console.log('CountrySelector mounted, refreshing countries');
    refreshCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCountryChange = (value: string) => {
    setCountry(value);
    
    // Check if we're on the insights/cards page and navigate to the new country's page
    if (location.pathname.includes('/cards/') || location.pathname.includes('/insights/')) {
      navigate(`/cards/${value}`);
    }
  };

  // Filter to only active countries
  const activeCountries = availableCountries.filter(country => country.active);
  
  // Find the current country name for display in the trigger
  const currentCountry = availableCountries.find(country => country.code === countryCode);

  return (
    <div className="flex items-center space-x-2">
      <Globe size={18} className="text-nation-blue" />
      <Select value={countryCode || 'global'} onValueChange={handleCountryChange}>
        <SelectTrigger className="w-[180px] h-9 border-nation-gray-light">
          <SelectValue>
            {countryCode ? (
              <div className="flex items-center gap-2">
                <CountryFlag countryCode={countryCode} />
                <span>{currentCountry?.name || countryCode}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Globe size={16} />
                <span>Global</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="global" className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <Globe size={16} />
              <span>Global</span>
            </div>
          </SelectItem>
          {activeCountries.map(country => (
            <SelectItem key={country.code} value={country.code} className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <CountryFlag countryCode={country.code} />
                <span>{country.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default CountrySelector;
